import React, { useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaPlus, FaEdit, FaTrash, FaDownload, FaEye } from "react-icons/fa";
import { API_URL } from "../../Constants";
import { AuthContext } from "../context/AuthContext";

const GreetingTemplates = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");

  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
  const [greetings, setGreetings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchGreetings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/greetingsTemplate/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGreetings(res.data);
    } catch (err) {
      console.error("Error fetching greetings:", err);
    }
  }, [companyId, token]);

  useEffect(() => {
    fetchGreetings();
  }, [fetchGreetings]);

  const handleSubmit = async () => {
    const formData = new FormData();
    if (file) formData.append("file", file);
    if (title) formData.append("title", title);
    if (message) formData.append("message", message);
    formData.append("companyId", companyId);

    try {
      if (editId) {
        await axios.put(`${API_URL}/api/greetingsTemplate/${editId}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/api/greetingsTemplate`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setShowModal(false);
      setTitle("");
      setMessage("");
      setFile(null);
      setEditId(null);
      fetchGreetings();
    } catch (err) {
      console.error("Error saving greeting:", err);
    }
  };

  const handleEdit = (template) => {
    setEditId(template.templateId);
    setTitle(template.title || "");
    setMessage(template.message || "");
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/greetingsTemplate/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteId(null);
      setShowDeleteModal(false);
      fetchGreetings();
    } catch (err) {
      console.error("Error deleting greeting:", err);
    }
  };

  const handleDownload = async (templateId) => {
    try {
      const response = await axios.get(`${API_URL}/api/greetingsTemplate/download/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "greeting_template_file");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const handleView = (templateId) => {
    window.open(`${API_URL}/api/greetingsTemplate/view/${templateId}`, "_blank");
  };

  return (
    <div className="p-2 mx-auto bg-gray-100 shadow-lg dark:bg-gray-900 dark:text-white">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Greeting Template Management</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          <FaPlus /> Add Template
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border rounded shadow text-sm">
          <thead>
            <tr className="bg-gray-50 text-left dark:bg-gray-900 dark:text-white">
              <th className="p-2">S No.</th>
              <th className="p-2">Title</th>
              <th className="p-2">Message</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {greetings.length > 0 ? (
              greetings.map((template, index) => (
                <tr key={template.templateId} className="border-t hover:bg-gray-50 dark:hover:bg-gray-500">
                  <td className="p-2">{index + 1}</td>
                  <td className="p-2">{template.title || "—"}</td>
                  <td className="p-2">{template.message || "—"}</td>
                  <td className="p-2 space-x-2">
                    <button onClick={() => handleView(template.templateId)} title="View" className="text-green-600 hover:text-green-800"><FaEye /></button>
                    <button onClick={() => handleDownload(template.templateId)} title="Download" className="text-blue-600 hover:text-blue-800"><FaDownload /></button>
                    <button onClick={() => handleEdit(template)} className="text-indigo-600 hover:text-indigo-800"><FaEdit /></button>
                    <button onClick={() => { setDeleteId(template.templateId); setShowDeleteModal(true); }} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-4 text-gray-500">No data found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-80 flex items-center justify-center z-50 ">
          <div className="bg-white w-full max-w-md mx-4 p-6 rounded shadow-lg dark:bg-gray-800 dark:text-white">
            <h3 className="text-xl font-semibold mb-4">{editId ? "Edit" : "Add"} Greeting Template</h3>
            <input
              type="text"
              className="w-full p-2 border rounded mb-4 dark:bg-gray-800"
              value={title}
              placeholder="Title"
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="w-full p-2 border rounded mb-4 dark:bg-gray-800"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
            />
            <input
              type="file"
              className="w-full mb-4"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditId(null);
                  setTitle("");
                  setMessage("");
                  setFile(null);
                }}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                {editId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-sm mx-4 p-6 rounded shadow-lg">
            <h3 className="text-lg font-semibold mb-4 text-center">Confirm Deletion</h3>
            <p className="text-center mb-6">Are you sure you want to delete this template?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId(null);
                }}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GreetingTemplates;
